import React, { useState, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage, FieldArray } from "formik";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import axios from "axios";
import { formatDateToDDMMMYYYY } from "../utils";
import { FaCheckSquare } from "react-icons/fa";
import {
  NotificationContainer,
  NotificationManager,
} from "react-notifications";
import "react-notifications/lib/notifications.css";
import styled from "styled-components";
import Header from "../components/Header/header";
import SecondHeader from "../components/Header/secondheader";
import Footer from "../components/Footer/index";
import { IndiaAIFestivalDeadlineNotice } from "../components/IndiaAIFestival";
import { BlinkingNode, LanguageSwitcher } from "../components/shared";
import {
  isSubmissionOpen,
  SUBMISSION_END_TIMES,
} from "../utils/impactFestival";

const StyledCheckIcon = styled(FaCheckSquare)`
  color: var(--success);
  font-size: 16px;
`;

const FlexCenter = styled.div`
  display: flex;
  align-items: center;
`;

const BlinkWrapper = styled.div`
  @media (min-width: 380px) and (max-width: 770px) {
    margin-top: 4rem;
  }

  @media (max-width: 379px) {
    margin-top: 5rem;
  }
`;

const unSdgs = [
  "No Poverty",
  "Zero Hunger",
  "Good Health and Well-Being",
  "Quality Education",
  "Gender Equality",
  "Clean Water and Sanitization",
  "Affordable and Clean Energy",
  "Decent Work and Economic Growth",
  "Industry, Innovation and Infrastructure",
  "Reduced Inequalities",
  "Sustainable Cities and Communities",
  "Responsible Consumption and Production",
  "Climate Action",
  "Life Below Water",
  "Life on Land",
  "Peace, Justice and Strong Institutions",
  "Partnerships for the Goals",
];

const projectTypes = ["Individual", "Group"];

const categories = [
  { label: "Impact Creators", value: "impactCreators" },
  { label: "Impact Shapers", value: "impactShapers" },
  { label: "Impact Nurturers", value: "impactNurturers" },
];

const subCategories = {
  impactCreators: [
    { label: "Student below 18 Years", value: "class_secondary" },
    { label: "Student above 18 Years", value: "class_sr_secondary" },
    { label: "AI for Accessibility", value: "ai_for_accessibility" },
  ],
  "": [],
  impactShapers: [],
  impactNurturers: [],
};

const IndiaAIFestivalRegistrationPage2025 = () => {
  const getSubmissionEndDate = process.env.REACT_APP_LAST_SUBMISSION_DATE;
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [canSubmitData, setCanSubmitData] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { t } = useTranslation();
  const year = 2025;

  useEffect(() => {
    const fetchCanSubmitData = async () => {
      try {
        const token = localStorage.getItem("jwtToken");

        const headers = {
          "x-access-token": token,
        };

        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/v2/impact-festival/can-submit`,
          { headers }
        );

        setCanSubmitData(response.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    // Function to fetch data from the API
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("jwtToken");

        const headers = {
          "x-access-token": token,
        };

        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/v2/impact-festival/entry`,
          { headers }
        );
        setData(response.data.data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    fetchCanSubmitData();
    fetchData();
  }, [isSubmitted]);

  const createFileUploadNotification = (type) => {
    switch (type) {
      case "submit_success":
        NotificationManager.success(
          "Project submitted!",
          "Congratulations! You have successfully submit your project for evaluatation.",
          3000
        );
        break;

      case "save_success":
        NotificationManager.success(
          "Congratulations!",
          "Your entry has been submitted successfully.",
          6000
        );
        break;

      default:
        NotificationManager.error(
          "Please try again or come back later.",
          "Uh-oh, there was an error!",
          6000
        );
        break;
    }
  };

  const editable = data?.status !== "confirmed";

  const RegistrationSchema = Yup.object().shape({
    category: Yup.string().required(t("validation.category")),
    subCategory: Yup.string().when(["category"], {
      is: (category) => !!category && subCategories[category].length > 0,
      then: (schema) => schema.required(t("validation.sub_category")),
    }),
    readGuideline: Yup.boolean()
      .oneOf([true], t("validation.read_guideline"))
      .required(t("validation.read_guideline")),

    focusedAiForAccessibility: Yup.string().when(["subCategory"], {
      is: (sub) => !!sub && sub === "ai_for_accessibility",
      then: (schema) =>
        schema.required(t("impact_festival_2025.question_2_validation")),
    }),

    projects: Yup.array().of(
      Yup.object().shape({
        isExisting: Yup.boolean(),
        sdg: Yup.string().when("isExisting", {
          is: false,
          then: (schema) =>
            schema
              .required(t("validation.sdg"))
              .test(
                "isNotPlaceholder",
                t("validation.sdg"),
                (value) => value !== ""
              ),
        }),
        ethicalConcern: Yup.string(),
        projectType: Yup.string().when(["..category", "isExisting"], {
          is: (category, isExisting) =>
            !isExisting && category === "impactCreators",
          then: (schema) => schema.required(t("validation.project_type")),
        }),
        members: Yup.array().when(["projectType", "isExisting"], {
          is: (projectType, isExisting) =>
            !isExisting && projectType === "Group",
          then: (schema) =>
            schema
              .of(Yup.string().required(t("validation.project_member")))
              .min(1, t("validation.at_least_one_member")),
        }),
        pdf: Yup.mixed()
          .nullable()
          .when("isExisting", {
            is: false,
            then: (schema) =>
              schema
                .test(
                  "pdf-required",
                  t("validation.project_pdf"),
                  function (value) {
                    const pathParts = this.path.match(/projects\[(\d+)\]/);
                    const index = pathParts ? parseInt(pathParts[1]) : 0;
                    return !!value || !!data.projects?.[index]?.pdf;
                  }
                )
                .test(
                  "pdf-size",
                  t("validation.file_too_large", { max: "5MB" }),
                  function (value) {
                    if (value && value.size) {
                      return value.size <= 5 * 1024 * 1024;
                    }
                    return true;
                  }
                )
                .test(
                  "pdf-format",
                  t("validation.invalid_file_type", { type: "PDF" }),
                  function (value) {
                    if (value && value.name) {
                      return value.name.toLowerCase().endsWith(".pdf");
                    }
                    return true;
                  }
                ),
          }),
        video: Yup.mixed()
          .nullable()
          .when("isExisting", {
            is: false,
            then: (schema) =>
              schema
                .test(
                  "video-or-url",
                  t("validation.either_project_video_or_url"),
                  function (value) {
                    const pathParts = this.path.match(/projects\[(\d+)\]/);
                    const index = pathParts ? parseInt(pathParts[1]) : 0;
                    const { videoURL } = this.parent;
                    return (
                      !!value || !!videoURL || !!data.projects?.[index]?.video
                    );
                  }
                )
                .test(
                  "video-size",
                  t("validation.file_too_large", { max: "25MB" }),
                  function (value) {
                    if (value && value.size) {
                      return value.size <= 25 * 1024 * 1024;
                    }
                    return true;
                  }
                )
                .test(
                  "video-format",
                  t("validation.invalid_file_type", {
                    type: "MP4, MOV, WMV, FLV, AVI",
                  }),
                  function (value) {
                    if (value && value.name) {
                      const allowedExtensions = [
                        ".mp4",
                        ".mov",
                        ".wmv",
                        ".flv",
                        ".avi",
                      ];
                      return allowedExtensions.some((ext) =>
                        value.name.toLowerCase().endsWith(ext)
                      );
                    }
                    return true;
                  }
                ),
          }),

        videoURL: Yup.string()
          .nullable()
          .when("isExisting", {
            is: false,
            then: (schema) =>
              schema.test(
                "video-or-url",
                t("validation.either_project_video_or_url"),
                function (value) {
                  const pathParts = this.path.match(/projects\[(\d+)\]/);
                  const index = pathParts ? parseInt(pathParts[1]) : 0;
                  const { video } = this.parent;

                  return (
                    !!value || !!video || !!data.projects?.[index]?.videoURL
                  );
                }
              ),
          }),
      })
    ),
  });

  const handleSubmit = async (values) => {
    const confirmed = window.confirm(t("submission_page_confirmation_text"));

    if (confirmed) {
      const formData = new FormData();

      formData.append("category", values.category);
      formData.append("subCategory", values.subCategory);
      formData.append("readGuideline", values.readGuideline);
      formData.append("identifyNeurodivergent", values.identifyNeurodivergent);
      formData.append(
        "focusedAiForAccessibility",
        values.focusedAiForAccessibility
      );
      formData.append("aimingChallenge", values.aimingChallenge);

      const existingProjectsCount = values.projects.filter(
        (p) => p.isExisting
      ).length;
      const newProjects = values.projects.filter((p) => !p.isExisting);

      newProjects.forEach((project, index) => {
        const projectIndex = existingProjectsCount + index;
        formData.append(
          `project[${projectIndex}][ethicalConcern]`,
          project.ethicalConcern
        );
        formData.append(`project[${projectIndex}][sdg]`, project.sdg);
        formData.append(
          `project[${projectIndex}][projectType]`,
          project.projectType
        );
        formData.append(`project[${projectIndex}][videoURL]`, project.videoURL);

        project.members.forEach((member) => {
          formData.append(`project[${projectIndex}][members][]`, member);
        });

        if (project.video) {
          formData.append(`project[${projectIndex}][video]`, project.video);
        }
        if (project.pdf) {
          formData.append(`project[${projectIndex}][pdf]`, project.pdf);
        }
        if (project.businessPlan) {
          formData.append(
            `businessPlan[${projectIndex}].pdf`,
            project.businessPlan
          );
        }
      });

      const source = localStorage.getItem("iaiif_source");
      if (source) {
        formData.append("source", source);
      }

      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/v2/impact-festival/confirm`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              "x-access-token": localStorage.getItem("jwtToken"),
            },
          }
        );
        createFileUploadNotification("save_success");
        setIsSubmitted(!isSubmitted);
      } catch (error) {
        createFileUploadNotification("error");
      }
    }
  };

  return (
    <>
      {localStorage.getItem("jwtToken") == null ? <Header /> : <SecondHeader />}
      {isSubmissionOpen(year) ? (
        <div className="iaiif-registration-page gray-light-bg">
          <div className="container d-flex justify-content-end">
            <BlinkWrapper>
              <LanguageSwitcher />
            </BlinkWrapper>
          </div>
          <div class="d-flex justify-content-center w-100">
            <BlinkingNode>
              <IndiaAIFestivalDeadlineNotice
                year={year}
                deadline={formatDateToDDMMMYYYY(SUBMISSION_END_TIMES[year])}
              />
            </BlinkingNode>
          </div>
          <div className="blog-container shadow">
            <div className="container">
              <div className="row user-info-container">
                <div className="col-md-12">
                  <h1 className="text-center">
                    {t("submission_page_title").replace("{year}", year)}
                  </h1>
                  <h2 className="text-center">
                    {t("submission_page_subtitle")}
                  </h2>
                </div>
              </div>
              <div>
                {loading ? (
                  <div className="d-flex align-items-center justify-content-center py-5">
                    <div class="spinner-grow" role="status">
                      <span class="sr-only">{t("loading_text")}</span>
                    </div>
                  </div>
                ) : (
                  <div>
                    {!canSubmitData.canSubmit ? (
                      <div className="alert alert-success">
                        {t("submission_page_max_allocated_project_exhaust")}
                      </div>
                    ) : (
                      data.status === "confirmed" && (
                        <div className="alert alert-success">
                          {t("submission_page_confirmation_v2")}
                        </div>
                      )
                    )}

                    <div className="p-2">
                      <p className="text-muted">
                        {t("submission_page_disclaimer_part_1")}{" "}
                        <a
                          href="/india-ai-impact-festival-faqs"
                          className="text-primary"
                        >
                          {t("submission_page_disclaimer_link_text_faq")}
                        </a>{" "}
                        {t("submission_page_disclaimer_part_2")}.
                      </p>
                    </div>

                    <Formik
                      enableReinitialize
                      initialValues={{
                        category: data.category || "",
                        subCategory: data.subCategory || "",
                        readGuideline: false,
                        identifyNeurodivergent:
                          data.identifyNeurodivergent || "",
                        focusedAiForAccessibility:
                          data.focusedAiForAccessibility || "",
                        aimingChallenge: data.aimingChallenge || "",
                        // Initialize with one project by default
                        projects:
                          data.projects && data.projects.length > 0
                            ? data.projects.map((project) => ({
                                ethicalConcern: project.ethicalConcern || "",
                                sdg: project.sdg || "",
                                members: project.members || [""],
                                projectType: project.projectType || "",
                                videoURL: project.videoURL || "",
                                video: project.video,
                                pdf: project.pdf,
                                businessPlan: project.businessPlan,
                                isExisting: true,
                              }))
                            : [
                                {
                                  ethicalConcern: "",
                                  sdg: "",
                                  members: [""],
                                  projectType: "",
                                  videoURL: "",
                                  video: null,
                                  pdf: null,
                                  businessPlan: null,
                                  isExisting: false,
                                },
                              ],
                      }}
                      validationSchema={RegistrationSchema}
                      onSubmit={(values) => handleSubmit(values)}
                    >
                      {({
                        setFieldValue,
                        values,
                        handleSubmit,
                        isSubmitting,
                      }) => {
                        console.log(values);

                        return (
                          <Form>
                            <div className="form-row flex-wrap">
                              <div
                                className={`col-12 col-md-${
                                  subCategories[values.category].length > 0
                                    ? "4"
                                    : "6"
                                } px-3 mb-3`}
                              >
                                <div className="form-group">
                                  <label htmlFor="studentName">
                                    {t("submission_page_label_name")}
                                  </label>
                                  <Field
                                    type="text"
                                    name="studentName"
                                    className="form-control bg-light m-0"
                                    id="studentName"
                                    placeholder={`${data.first_name} ${data.last_name}`}
                                    readOnly
                                  />
                                </div>
                              </div>

                              <div
                                className={`col-12 col-md-${
                                  subCategories[values.category].length > 0
                                    ? "4"
                                    : "6"
                                } px-3 mb-3`}
                              >
                                <div className="form-group">
                                  <label htmlFor="category">
                                    {t("submission_page_label_category")}
                                    <small className="text-danger">*</small>
                                  </label>
                                  <Field
                                    as="select"
                                    name="category"
                                    className="form-control m-0"
                                    id="category"
                                    disabled={!editable}
                                  >
                                    <option value="" disabled selected hidden>
                                      {t(
                                        "submission_page_label_category_option_blank"
                                      )}
                                    </option>
                                    {categories.map((category) => (
                                      <option
                                        key={category.value}
                                        value={category.value}
                                      >
                                        {t(
                                          `submission_page_category_options.${category.value}`
                                        )}
                                      </option>
                                    ))}
                                  </Field>
                                  <ErrorMessage
                                    name="category"
                                    component="div"
                                    className="text-danger"
                                  />
                                </div>
                              </div>
                              {values.category &&
                                subCategories[values.category].length > 0 && (
                                  <div className={`col-12 col-md-4 px-3 mb-3`}>
                                    <div className="form-group">
                                      <label htmlFor="subCategory">
                                        {t(
                                          "submission_page_label_sub_category"
                                        )}
                                        <small className="text-danger">*</small>
                                      </label>
                                      <Field
                                        as="select"
                                        name="subCategory"
                                        className="form-control m-0"
                                        id="subCategory"
                                        disabled={!editable}
                                      >
                                        <option
                                          value=""
                                          disabled
                                          selected
                                          hidden
                                        >
                                          {t(
                                            "submission_page_label_sub_category_option_blank"
                                          )}
                                        </option>
                                        {subCategories[values.category]?.map(
                                          (category) => (
                                            <option
                                              key={category.value}
                                              value={category.value}
                                            >
                                              {t(
                                                `impact_festival_2025.submission_page_sub_category_options.${category.value}`
                                              )}
                                            </option>
                                          )
                                        )}
                                      </Field>
                                      <ErrorMessage
                                        name="subCategory"
                                        component="div"
                                        className="text-danger"
                                      />
                                    </div>
                                  </div>
                                )}
                              {values.subCategory ===
                                "ai_for_accessibility" && (
                                <>
                                  {/* Question 1 */}
                                  <div className="col-md-6 mb-3 px-3">
                                    <label>
                                      {t("impact_festival_2025.question_1")}
                                      <small>
                                        {t("impact_festival_2025.optional")}
                                      </small>
                                    </label>
                                    <div className="form-group">
                                      <div className="form-check form-check-inline">
                                        <Field
                                          type="radio"
                                          name="identifyNeurodivergent"
                                          value="yes"
                                          id="identifyNeurodivergent-yes"
                                          className="form-check-input"
                                          disabled={!editable}
                                        />
                                        <label
                                          className="form-check-label"
                                          htmlFor="identifyNeurodivergent-yes"
                                        >
                                          {t(
                                            "submission_page_special_award_option_yes"
                                          )}
                                        </label>
                                      </div>
                                      <div className="form-check form-check-inline">
                                        <Field
                                          type="radio"
                                          name="identifyNeurodivergent"
                                          value="no"
                                          id="identifyNeurodivergent-no"
                                          className="form-check-input"
                                          disabled={!editable}
                                        />
                                        <label
                                          className="form-check-label"
                                          htmlFor="identifyNeurodivergent-no"
                                        >
                                          {t(
                                            "submission_page_special_award_option_no"
                                          )}
                                        </label>
                                      </div>
                                      <div className="form-check form-check-inline">
                                        <Field
                                          type="radio"
                                          name="identifyNeurodivergent"
                                          value="notToSay"
                                          id="identifyNeurodivergent-notToSay"
                                          className="form-check-input"
                                          disabled={!editable}
                                        />
                                        <label
                                          className="form-check-label"
                                          htmlFor="identifyNeurodivergent-notToSay"
                                        >
                                          {t("impact_festival_2025.notToSay")}
                                        </label>
                                      </div>
                                      <ErrorMessage
                                        name="identifyNeurodivergent"
                                        component="div"
                                        className="text-danger"
                                      />
                                      <small className="form-text text-danger mt-2">
                                        {t("impact_festival_2025.note_2")}
                                      </small>
                                    </div>
                                  </div>

                                  <div className="d-flex flex-wrap">
                                    <div className="col-md-6 mb-3 px-3">
                                      <label>
                                        {t("impact_festival_2025.question_2")}
                                        {t(
                                          "impact_festival_2025.question_2_description"
                                        )}
                                        <small className="text-danger">*</small>
                                      </label>
                                      <div className="form-group">
                                        <div className="form-check form-check-inline">
                                          <Field
                                            type="radio"
                                            name="focusedAiForAccessibility"
                                            value="yes"
                                            id="focusedAiForAccessibility-yes"
                                            className="form-check-input"
                                            disabled={!editable}
                                          />
                                          <label
                                            className="form-check-label"
                                            htmlFor="focusedAiForAccessibility-yes"
                                          >
                                            {t(
                                              "submission_page_special_award_option_yes"
                                            )}
                                          </label>
                                        </div>
                                        <div className="form-check form-check-inline">
                                          <Field
                                            type="radio"
                                            name="focusedAiForAccessibility"
                                            value="no"
                                            id="focusedAiForAccessibility-no"
                                            className="form-check-input"
                                            disabled={!editable}
                                          />
                                          <label
                                            className="form-check-label"
                                            htmlFor="focusedAiForAccessibility-no"
                                          >
                                            {t(
                                              "submission_page_special_award_option_no"
                                            )}
                                          </label>
                                        </div>
                                        <ErrorMessage
                                          name="focusedAiForAccessibility"
                                          component="div"
                                          className="text-danger"
                                        />
                                      </div>
                                    </div>

                                    {/* Question 3 */}
                                    <div className="col-md-6 mb-3 px-3">
                                      <label>
                                        {t("impact_festival_2025.question_3")}
                                      </label>
                                      <Field
                                        type="text"
                                        name="aimingChallenge"
                                        disabled={data.aimingChallenge}
                                        className="form-control m-0"
                                        id="aiming-challenge"
                                      />
                                      <ErrorMessage
                                        name="aimingChallenge"
                                        component="div"
                                        className="text-danger"
                                      />
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>

                            {/* Projects section - dynamic */}
                            <FieldArray name="projects">
                              {({ push, remove }) => (
                                <>
                                  {values.projects?.map((project, index) => (
                                    <div
                                      key={index}
                                      className="project-section mb-4 p-3 border rounded"
                                    >
                                      <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h5>Project {index + 1}</h5>
                                        {index > 0 && !project.isExisting && (
                                          <button
                                            type="button"
                                            className="btn btn-danger btn-sm"
                                            onClick={() => remove(index)}
                                          >
                                            Remove
                                          </button>
                                        )}
                                      </div>

                                      <div className="form-row">
                                        <div className="col-md-6 px-3 mb-3">
                                          <div className="form-group">
                                            <label htmlFor={`sdg-${index}`}>
                                              {t("submission_page_label_sdg")}
                                              <small className="text-danger">
                                                *
                                              </small>
                                            </label>
                                            <Field
                                              as="select"
                                              name={`projects[${index}].sdg`}
                                              className="form-control m-0"
                                              id={`sdg-${index}`}
                                              disabled={
                                                !editable && project.isExisting
                                              }
                                            >
                                              <option value="">
                                                {t(
                                                  "submission_page_sdg_option_blank"
                                                )}
                                              </option>
                                              {unSdgs.map((sdg, idx) => (
                                                <option
                                                  key={sdg}
                                                  value={`SDG ${
                                                    idx + 1
                                                  }: ${sdg}`}
                                                >
                                                  {`SDG ${idx + 1}: ${t(
                                                    "submission_page_sdg_options." +
                                                      idx
                                                  )}`}
                                                </option>
                                              ))}
                                            </Field>
                                            <ErrorMessage
                                              name={`projects[${index}].sdg`}
                                              component="div"
                                              className="text-danger"
                                            />
                                          </div>
                                        </div>

                                        <div className="col-md-6 px-3 mb-3">
                                          <div className="form-group">
                                            <label
                                              htmlFor={`ethicalConcern-${index}`}
                                            >
                                              {t(
                                                "submission_page_label_ethical_concern"
                                              )}
                                            </label>
                                            <Field
                                              type="text"
                                              name={`projects[${index}].ethicalConcern`}
                                              className="form-control m-0"
                                              id={`ethicalConcern-${index}`}
                                              disabled={
                                                !editable && project.isExisting
                                              }
                                              placeholder={t(
                                                "submission_page_label_ethical_concern_placeholder"
                                              )}
                                            />
                                            <ErrorMessage
                                              name={`projects[${index}].ethicalConcern`}
                                              component="div"
                                              className="text-danger"
                                            />
                                          </div>
                                        </div>
                                      </div>

                                      {values.category === "impactCreators" && (
                                        <div className="form-row">
                                          <div className="col-md-6 px-3 mb-3">
                                            <div className="form-group">
                                              <label
                                                htmlFor={`projectType-${index}`}
                                              >
                                                {t(
                                                  "submission_page_label_project_type"
                                                )}
                                                <small className="text-danger">
                                                  *
                                                </small>
                                              </label>
                                              <Field
                                                as="select"
                                                name={`projects[${index}].projectType`}
                                                className="form-control m-0"
                                                id={`projectType-${index}`}
                                                disabled={
                                                  !editable &&
                                                  project.isExisting
                                                }
                                              >
                                                <option value="">
                                                  {t(
                                                    "submission_page_project_type_option_blank"
                                                  )}
                                                </option>
                                                {projectTypes.map(
                                                  (projectType, i) => (
                                                    <option
                                                      key={projectType}
                                                      value={projectType}
                                                    >
                                                      {t(
                                                        "submission_page_project_type_options." +
                                                          i
                                                      )}
                                                    </option>
                                                  )
                                                )}
                                              </Field>
                                              <ErrorMessage
                                                name={`projects[${index}].projectType`}
                                                component="div"
                                                className="text-danger"
                                              />
                                            </div>
                                          </div>

                                          {project.projectType === "Group" && (
                                            <div className="col-md-6 px-3 mb-3">
                                              <div className="form-group">
                                                <label>
                                                  Name and class of team members
                                                  (max 3)
                                                  <small className="text-danger">
                                                    *
                                                  </small>
                                                </label>
                                                <FieldArray
                                                  name={`projects[${index}].members`}
                                                >
                                                  {({
                                                    push: pushMember,
                                                    remove: removeMember,
                                                  }) => (
                                                    <>
                                                      {project.members?.map(
                                                        (
                                                          member,
                                                          memberIndex
                                                        ) => (
                                                          <div
                                                            key={memberIndex}
                                                            className="d-flex align-items-center mb-2"
                                                          >
                                                            <Field
                                                              type="text"
                                                              name={`projects[${index}].members[${memberIndex}]`}
                                                              className="form-control m-0"
                                                              disabled={
                                                                !editable &&
                                                                project.isExisting
                                                              }
                                                              placeholder="Team member name and class"
                                                            />
                                                            {memberIndex > 0 &&
                                                              !project.isExisting && (
                                                                <button
                                                                  type="button"
                                                                  className="btn btn-danger btn-sm ml-2"
                                                                  onClick={() =>
                                                                    removeMember(
                                                                      memberIndex
                                                                    )
                                                                  }
                                                                >
                                                                  ×
                                                                </button>
                                                              )}
                                                          </div>
                                                        )
                                                      )}
                                                      {project.members?.length <
                                                        3 && (
                                                        <button
                                                          type="button"
                                                          className="btn btn-secondary btn-sm"
                                                          onClick={() =>
                                                            pushMember("")
                                                          }
                                                        >
                                                          Add Member
                                                        </button>
                                                      )}
                                                    </>
                                                  )}
                                                </FieldArray>
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      )}

                                      <div className="form-row">
                                        <div className="col-md-6 px-3 mb-3">
                                          <div className="form-group">
                                            <label
                                              htmlFor={`projectPDF-${index}`}
                                            >
                                              {t(
                                                "submission_page_label_submit_pdf_1"
                                              )}{" "}
                                              {values.category ===
                                              "impactCreators"
                                                ? t(
                                                    "submission_page_label_submit_pdf_project"
                                                  )
                                                : t(
                                                    "submission_page_label_submit_pdf_poster"
                                                  )}
                                              <small className="text-danger">
                                                *
                                              </small>
                                            </label>
                                            {data.projects?.[index]?.pdf && (
                                              <FlexCenter>
                                                <StyledCheckIcon />{" "}
                                                <small className="text-success ml-1">
                                                  {t(
                                                    "submission_page_label_submitted_text"
                                                  )}
                                                </small>
                                              </FlexCenter>
                                            )}

                                            {!project.isExisting && (
                                              <>
                                                <input
                                                  id={`projectPDF-${index}`}
                                                  name={`projects[${index}].pdf`}
                                                  type="file"
                                                  onChange={(event) =>
                                                    setFieldValue(
                                                      `projects[${index}].pdf`,
                                                      event.currentTarget
                                                        .files[0]
                                                    )
                                                  }
                                                  className="form-control-file mt-2"
                                                  disabled={
                                                    !editable &&
                                                    project.isExisting
                                                  }
                                                />
                                                <small class="form-text text-muted">
                                                  {t(
                                                    "submission_page_submit_pdf_requirement"
                                                  )}
                                                </small>
                                              </>
                                            )}

                                            <ErrorMessage
                                              name={`projects[${index}].pdf`}
                                              component="div"
                                              className="text-danger"
                                            />
                                          </div>

                                          <div className="form-group">
                                            <label
                                              htmlFor={`businessPlan-${index}`}
                                            >
                                              {t(
                                                "submission_page_label_submit_business_plan"
                                              )}
                                            </label>
                                            {data.projects?.[index]?.pdf && (
                                              <FlexCenter>
                                                <StyledCheckIcon />{" "}
                                                <small className="text-success ml-1">
                                                  {t(
                                                    "submission_page_label_submitted_text"
                                                  )}
                                                </small>
                                              </FlexCenter>
                                            )}

                                            {!project.isExisting && (
                                              <>
                                                <input
                                                  id={`businessPlan-${index}`}
                                                  name={`projects[${index}].businessPlan`}
                                                  type="file"
                                                  onChange={(event) =>
                                                    setFieldValue(
                                                      `projects[${index}].businessPlan`,
                                                      event.currentTarget
                                                        .files[0]
                                                    )
                                                  }
                                                  className="form-control-file mt-2"
                                                  disabled={
                                                    !editable &&
                                                    project.isExisting
                                                  }
                                                />
                                                <small class="form-text text-muted">
                                                  {t(
                                                    "submission_page_submit_pdf_requirement"
                                                  )}
                                                </small>
                                              </>
                                            )}

                                            <ErrorMessage
                                              name={`projects[${index}].businessPlan`}
                                              component="div"
                                              className="text-danger"
                                            />
                                          </div>
                                        </div>

                                        <div className="col-md-6 px-3 mb-3">
                                          <div className="form-group">
                                            <label
                                              htmlFor={`projectVideo-${index}`}
                                            >
                                              {t(
                                                "submission_page_label_submit_video"
                                              )}
                                              <small className="text-danger">
                                                *
                                              </small>
                                            </label>

                                            {data.projects?.[index]?.video && (
                                              <FlexCenter>
                                                <StyledCheckIcon />{" "}
                                                <small className="text-success ml-1">
                                                  {t(
                                                    "submission_page_label_submitted_text"
                                                  )}
                                                </small>
                                              </FlexCenter>
                                            )}

                                            {!project.isExisting && (
                                              <>
                                                <input
                                                  id={`projectVideo-${index}`}
                                                  name={`projects[${index}].video`}
                                                  type="file"
                                                  onChange={(event) =>
                                                    setFieldValue(
                                                      `projects[${index}].video`,
                                                      event.currentTarget
                                                        .files[0]
                                                    )
                                                  }
                                                  className="form-control-file mt-2"
                                                  disabled={
                                                    !editable &&
                                                    project.isExisting
                                                  }
                                                />
                                                <small class="form-text text-muted">
                                                  {t(
                                                    "submission_page_submit_video_requirement"
                                                  )}
                                                </small>
                                              </>
                                            )}

                                            <ErrorMessage
                                              name={`projects[${index}].video`}
                                              component="div"
                                              className="text-danger"
                                            />
                                          </div>
                                          {!project.isExisting && (
                                            <div className="form-group">
                                              <label
                                                htmlFor={`videoURL-${index}`}
                                              >
                                                {t(
                                                  "submission_page_label_submit_video_url"
                                                )}
                                              </label>
                                              <small className="form-text">
                                                {t(
                                                  "submission_page_submit_video_url_description"
                                                )}
                                              </small>
                                              <>
                                                <Field
                                                  type="text"
                                                  name={`projects[${index}].videoURL`}
                                                  className="form-control m-0 mb-2"
                                                  id={`videoURL-${index}`}
                                                  disabled={
                                                    !editable &&
                                                    project.isExisting
                                                  }
                                                  placeholder="E.g., https://www.youtube.com/watch?v=HGYYEUSm-0Q"
                                                />
                                              </>
                                              <ErrorMessage
                                                name={`projects[${index}].videoURL`}
                                                component="div"
                                                className="text-danger"
                                              />
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                      <div className="form-row">
                                        <div className="col-12">
                                          <p
                                            className="mb-0"
                                            style={{ color: "#cc0000" }}
                                          >
                                            {t("submission_page_label_note")}:{" "}
                                            {t("submission_page_note_project")}
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  ))}

                                  {values.projects.length <
                                    canSubmitData?.maxAllowed && (
                                    <div className="mb-4">
                                      <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={() =>
                                          push({
                                            ethicalConcern: "",
                                            sdg: "",
                                            members: [""],
                                            projectType: "",
                                            videoURL: "",
                                            pdf: null,
                                            video: null,
                                            isExisting: false,
                                          })
                                        }
                                      >
                                        Add Another Project
                                      </button>
                                    </div>
                                  )}
                                </>
                              )}
                            </FieldArray>

                            {/* Terms and conditions */}
                            <div className="form-row">
                              <div class="col-12 px-3 mb-md-0">
                                <div className="form-group">
                                  <div className="form-check form-check-inline">
                                    <Field
                                      type="checkbox"
                                      disabled={
                                        !editable && !canSubmitData?.canSubmit
                                      }
                                      className="form-check-input"
                                      name="readGuideline"
                                      id="readGuideline"
                                    />
                                    <label
                                      htmlFor="readGuideline"
                                      className="form-check-label"
                                    >
                                      {t("submission_page_terms_check_text")}{" "}
                                      <a
                                        href="/india-ai-impact-festival-faqs"
                                        className="text-primary"
                                      >
                                        <strong>
                                          {t(
                                            "submission_page_disclaimer_link_text_faq"
                                          )}
                                        </strong>
                                      </a>
                                      .
                                    </label>
                                  </div>
                                  <ErrorMessage
                                    name="readGuideline"
                                    component="div"
                                    className="text-danger"
                                  />
                                </div>
                              </div>
                            </div>

                            {canSubmitData?.canSubmit &&
                              values.projects.some((p) => !p.isExisting) && (
                                <div className="mt-2">
                                  <div>
                                    <button
                                      type="button"
                                      onClick={() => handleSubmit()}
                                      className="btn btn-primary mr-3 mt-3 mt-md-0 "
                                    >
                                      {isSubmitting && (
                                        <span
                                          class="spinner-grow spinner-grow-sm mr-2"
                                          role="status"
                                          aria-hidden="true"
                                        ></span>
                                      )}
                                      {isSubmitting
                                        ? t(
                                            "submission_page_submitting_btn_text"
                                          )
                                        : t("submission_page_submit_btn_text")}
                                    </button>
                                  </div>
                                  {values.category && (
                                    <small className="text-muted font-italic pt-2">
                                      {t("submission_page_download_text")}{" "}
                                      <a
                                        className="text-black"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        href={
                                          values.category === "impactCreators"
                                            ? "/assets/pdf/evaluation-impact-creators.pdf"
                                            : "/assets/pdf/evaluation-impact-shapers-nurturers.pdf"
                                        }
                                      >
                                        <strong>
                                          {t(
                                            "submission_page_evaluation_params_text"
                                          )}
                                        </strong>
                                      </a>
                                      .
                                    </small>
                                  )}
                                </div>
                              )}
                          </Form>
                        );
                      }}
                    </Formik>
                  </div>
                )}
              </div>
            </div>
          </div>
          <NotificationContainer />
        </div>
      ) : (
        <div className="iaiif-registration-page gray-light-bg footer-sticky-bottom">
          <div className="blog-container shadow">
            <div className="container">
              <div className="row align-items-center user-info-container">
                <div className="col-md-12">
                  <h1 className="text-center">{t("submission_page_title")}</h1>
                </div>
              </div>
              <div className="row justify-content-center">
                <div className="col-12 col-md-9 col-xl-7">
                  <div className="alert alert-info mx-auto my-3 my-md-5">
                    Submissions closed.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <Footer />
    </>
  );
};

export default IndiaAIFestivalRegistrationPage2025;
